import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Star, Trash2 } from "lucide-react";
import { api, errorMessage } from "../../lib/api";
import { Field, TextInput } from "../forms/Fields";

const ResourceStats = ({ s, id }) => {
  if (!s) return null;
  const items = [["Downloads", s.downloads], ["People", s.people], ["Want a follow-up", s.follow_ups]];
  return (
    <div className="mt-3" data-testid={`resource-stats-${id}`}>
      <dl className="flex flex-wrap gap-2">
        {items.map(([k, v]) => (
          <div key={k} className={`rounded-full px-3 py-1 text-sm ${k === "Want a follow-up" && v > 0 ? "bg-teal text-paper" : "bg-sky"}`}>
            <dt className="inline">{k}: </dt><dd className="inline font-semibold">{v}</dd>
          </div>
        ))}
      </dl>
      {s.last_download && <p className="mt-1.5 text-xs text-slate">Last download {new Date(s.last_download).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</p>}
    </div>
  );
};

export const ResourcesPanel = () => {
  const [items, setItems] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const [stats, setStats] = useState({});
  const load = () => {
    api.get("/resources").then((r) => setItems(r.data)).catch((e) => toast.error(errorMessage(e)));
    api.get("/admin/resources/stats").then((r) => setStats(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Choose a file to upload");
    const fd = new FormData();
    fd.append("title", title); fd.append("description", description); fd.append("file", file);
    setBusy(true);
    try { await api.post("/admin/resources", fd); toast.success("Resource uploaded"); setTitle(""); setDescription(""); fileRef.current.value = ""; load(); }
    catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  const feature = async (r) => {
    try { const { data } = await api.put(`/admin/resources/${r.id}/feature`); toast.success(data.featured ? "Now featured on the Home page" : "No longer featured"); load(); } catch (err) { toast.error(errorMessage(err)); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Remove "${r.title}" from the website?`)) return;
    try { await api.delete(`/admin/resources/${r.id}`); toast.success("Resource removed"); load(); } catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <div className="grid lg:grid-cols-[minmax(0,26rem)_1fr] gap-10 font-sans" data-testid="admin-resources">
      <form onSubmit={upload} className="bg-paper rounded-2xl p-6 space-y-5 self-start" aria-label="Upload resource" data-testid="resource-upload-form">
        <p className="font-display text-2xl">Upload a resource</p>
        <Field id="r-title" label="Title" required><TextInput id="r-title" required value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
        <Field id="r-desc" label="Short description"><TextInput id="r-desc" value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
        <div>
          <label htmlFor="r-file" className="lbl block">File <span className="text-teal" aria-hidden="true">*</span></label>
          <input id="r-file" ref={fileRef} type="file" accept=".pdf,.docx,.pptx,.jpg,.jpeg,.png,.webp" className="mt-2 block w-full text-sm file:mr-3 file:rounded-full file:border-0 file:bg-sky file:px-4 file:py-2" data-testid="resource-file-input" />
          <p className="mt-1 text-sm text-slate">PDF, Word, PowerPoint or image, up to 20 MB.</p>
        </div>
        <button type="submit" disabled={busy} className="w-full inline-flex justify-center items-center gap-2 bg-teal text-paper rounded-full py-3 hover:bg-teal-deep disabled:opacity-70" data-testid="resource-upload-button">
          {busy && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}{busy ? "Uploading..." : "Upload"}
        </button>
      </form>
      <div className="min-w-0">
        <p className="font-display text-2xl">On the Resources page</p>
        <p className="mt-1 text-sm text-slate">Tap the star to feature one resource on the Home page. Visitors enter their name and email before downloading; you'll find them under Requests.</p>
        {items?.length === 0 && <p className="mt-4 text-slate">No resources yet. The Resources page shows a friendly "coming soon" message until you add one.</p>}
        <ul className="mt-4 space-y-3">
          {(items || []).map((r) => (
            <li key={r.id} className="bg-paper rounded-2xl p-4 flex items-center justify-between gap-3" data-testid={`admin-resource-${r.id}`}>
              <div className="min-w-0"><p className="font-semibold truncate">{r.title}</p><p className="text-sm text-slate truncate">{r.filename} · {(r.size / 1024).toFixed(0)} KB</p>
                <ResourceStats s={stats[r.id]} id={r.id} />
              </div>
              <span className="flex items-center gap-1 shrink-0">
              <button onClick={() => feature(r)} aria-pressed={r.featured} aria-label={r.featured ? `Stop featuring ${r.title} on the Home page` : `Feature ${r.title} on the Home page`} className={`p-2 rounded-full hover:bg-sky ${r.featured ? "text-brass" : ""}`} data-testid={`resource-feature-${r.id}`}><Star className="w-4 h-4" fill={r.featured ? "currentColor" : "none"} /></button>
              <button onClick={() => remove(r)} aria-label={`Remove ${r.title}`} className="p-2 rounded-full hover:bg-sky" data-testid={`resource-remove-${r.id}`}><Trash2 className="w-4 h-4" /></button>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
